// Design tokens. Use these in any code that wants direct token access without
// going through `useToken()`. AntD components automatically pick the matching
// values from `antdTheme` (see `./antdTheme.ts`).

export const COLORS = {
  // Surfaces
  bgPage:    "#f5f5f5",
  bgCard:    "#ffffff",
  bgSubtle:  "#fafafa",
  bgHover:   "#f5f9ff",
  bgActive:  "#e6f4ff",

  // Borders
  borderDefault: "#f0f0f0",
  borderStrong:  "#d9d9d9",
  divider:       "#e8e8e8",
  borderFocus:   "#4096ff",

  // Text
  text:          "rgba(0, 0, 0, 0.88)",
  textSecondary: "rgba(0, 0, 0, 0.65)",
  textTertiary:  "rgba(0, 0, 0, 0.45)",
  textDisabled:  "rgba(0, 0, 0, 0.25)",
  textInverse:   "#ffffff",

  // Brand
  primary:       "#1677ff",
  primaryHover:  "#4096ff",
  primaryActive: "#0958d9",
  primaryBg:     "#e6f4ff",
  primaryBorder: "#91caff",

  // Semantic
  success:       "#52c41a",
  successText:   "#389e0d",
  successBg:     "#f6ffed",
  successBorder: "#b7eb8f",

  warning:       "#faad14",
  warningText:   "#d48806",
  warningBg:     "#fff7e6",
  warningBorder: "#ffe58f",

  error:         "#ff4d4f",
  errorText:     "#cf1322",
  errorBg:       "#fff1f0",
  errorBorder:   "#ffccc7",

  info:          "#1890ff",
  infoBg:        "#e6f4ff",

  // Domain accents
  cost:       "#cf1322",   // Negative prices, costs, received invoices
  revenue:    "#389e0d",   // Positive prices, issued invoices
  correction: "#d48806",   // Correction deltas, changed values
  warningStrong: "#fa8c16", // Expiring soon, orange (≠ COLORS.warning yellow)
  cyan:       "#13c2c2",   // Cyan badge/tab
  purple:     "#722ed1",   // Purple (e.g. reconcile forced)
  neutral:    "#8c8c8c",   // Same as iconNeutral / antd colorTextSecondary
  // Aliases to existing colors — kept for clarity at call sites.
  // SEMANTIC_COLORS.valid → success, .danger → error, .amber → warning, .info → info
  // .primary already exists.

  // Side direction colors (saturated; for invoice direction / pricing side / side-tagged cards).
  // Distinct from txColumn*Tint which are near-white structural tints.
  sideLoadBg:        "#f0f5ff",
  sideLoadBorder:    "#d6e4ff",
  sideLoadGradient:  "linear-gradient(180deg, #f0f5ff, #fff)",
  sideUnloadBg:      "#fff7e6",
  sideUnloadBorder:  "#ffe7ba",
  sideUnloadGradient:"linear-gradient(180deg, #fff7e6, #fff)",

  // Transaction-layout column tints (NOT invoice direction — near-white structural)
  txColumnLoadTint:   "#fffdf8",
  txColumnUnloadTint: "#f9fff4",
  transportTint:      "#f5f8ff",

  // Status / state backgrounds (warningBg already declared above)
  warningBgStrong:  "#fffbe6",
  expiredRowBg:     "#ffccc7",
  pinnedBg:         "#f0f5ff",   // Shares sideLoadBg value (pinned reads as directionally important)
  draggableHoverBg: "#e6f7ff",

  // Status / state borders
  borderWarning:    "#ffe58f",
  borderSuccess:    "#b7eb8f",
  borderPinned:     "#d6e4ff",
  borderConflict:   "#fff1b8",

  // Icon colors
  iconNeutral:      "#8c8c8c",
  iconMuted:        "#bfbfbf",
  iconDisabled:     "#d9d9d9",

  // Toolbar
  toolbarInputFillBg: "#f5f5f5",
} as const;

export const SPACE = {
  xs:  4,
  sm:  8,
  md:  12,
  lg:  16,
  xl:  24,
  xxl: 32,
} as const;

export const FONT_SIZE = {
  micro:      10,
  label:      11,
  small:      12,
  caption:    12.5,  // editorial sub-line (AmountHero / Stat sub) — half-pixel intentional
  body:       13,
  normal:     14,
  subheading: 16,
  large:      18,
  h2:         22,
  h1:         28,
  // Editorial display sizes — hero amounts, marquee numbers (Phase B/C primitives).
  display:    32,    // AmountHero / hero amounts (between h1 and hero)
  hero:       36,
  marquee:    48,
} as const;

export const FONT_WEIGHT = {
  regular:  400,
  medium:   500,
  semibold: 600,
  bold:     700,
} as const;

export const LINE_HEIGHT = {
  tight:   1.3,
  normal:  1.5,
  loose:   1.6,
  // Editorial — hero displays + sub-text (Phase B/C).
  display: 1.05,    // AmountHero value
  snug:    1.15,    // Stat value
  relaxed: 1.4,     // sub-text (StatusDot, AmountHero sub, Stat sub)
} as const;

// Letter-spacing scale — editorial typography (Phase B/C primitives).
// AntD doesn't ship a letter-spacing token; this is the project surface.
export const LETTER_SPACING = {
  eyebrow:      "0.12em",   // uppercase eyebrow labels
  tightDisplay: "-0.02em",  // AmountHero value
  tightStat:    "-0.01em",  // Stat value
  tightHero:    "-0.025em", // page hero title (entity detail H1, 36px)
} as const;

export const RADIUS = {
  xs:   4,
  sm:   6,
  md:   6,
  lg:   8,
  pill: 999,
} as const;

export const SHADOW = {
  xs:          "0 1px 2px rgba(0, 0, 0, 0.04)",
  sm:          "0 2px 4px rgba(0, 0, 0, 0.06)",
  md:          "0 4px 12px rgba(0, 0, 0, 0.08)",
  lg:          "0 8px 24px rgba(0, 0, 0, 0.12)",
  focus:       "0 0 0 2px rgba(22, 119, 255, 0.12)",
  focusError:  "0 0 0 2px rgba(255, 77, 79, 0.12)",
} as const;

export const CONTROL_HEIGHT = {
  sm: 24,
  md: 32,
  lg: 40,
} as const;

export const MOTION = {
  durationFast: 120,
  durationMid:  200,
  easeOut:      "cubic-bezier(0.4, 0, 0.2, 1)",
} as const;

export const FONT_FAMILY = {
  sans: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  mono: "'SF Mono', Menlo, Monaco, Consolas, 'Liberation Mono', monospace",
  // Editorial typography (Phase B/C). `inter` leads with the Inter webfont; `serif` is for
  // marquee numbers, hero amounts, and entity titles per the __design language.
  inter: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  serif: "'Iowan Old Style', 'Palatino', 'Charter', Georgia, serif",
} as const;

// Layout primitives — paddings, peek widths, toolbar widths, off-scale margins.
// Use these for layout-sized values that don't fit SPACE/RADIUS/etc.
export const LAYOUT = {
  // Canonical paddings
  paddingHeader:  "12px 16px",
  paddingSection: "8px 12px",
  paddingCompact: "6px 12px",
  paddingHeaderXY:  { x: 16, y: 12 },
  paddingSectionXY: { x: 12, y: 8 },
  paddingCompactXY: { x: 12, y: 6 },

  // Off-scale margins (kept distinct from SPACE.*)
  marginFormItem:     8,    // Form.Item override in compact modal forms
  marginHeaderBottom: 20,   // DrawerHeader bottom margin (intentionally > SPACE.lg)

  // Peek panel
  peekDefaultWidth: 580,
  peekMinWidth:     420,
  peekMaxWidth:     720,

  // Toolbar
  toolbarSearchWidth: 280,

  // Editorial layout (Phase B/C — DetailLayout, ListLayout, BlueprintShell).
  // Spacious cadence per __design language. Opt-in per layout, not global.
  contentMaxWidth: 1440,    // 1440px cap = full width on 15" MBP; constrains only on wider monitors
  pageGutterX:     "clamp(16px, 3vw, 48px)",
  editorial: {
    // Page-level cadence
    sectionGap: 56,
    statGap:    40,
    tableRow:   20,

    // Component-internal spacing (AmountHero, Stat, Eyebrow callers)
    labelGap:     6,    // gap below eyebrow → primary value
    statLabelGap: 10,   // gap below eyebrow → value (Stat cells)
    statValueGap: 12,   // gap between value and progress bar
    statSubGap:   8,    // gap between value and sub
    statPadY:     22,   // top padding of Stat cell
    statPadYBot:  24,   // bottom padding of Stat cell
    statPadX:     28,   // horizontal padding of Stat cell

    // Progress bar (Stat)
    progressH:    5,
    progressR:    3,

    // Page chrome (DetailLayout / ListLayout)
    pagePadX:        28,   // horizontal page padding
    pagePadY:        20,   // vertical content padding (DetailLayout body top)
    pageBottomPad:   60,   // extra bottom padding so the footer doesn't crowd the last block
    breadcrumbPadY:  10,   // vertical padding of breadcrumb / toolbar strips (ListLayout)

    // EntityViewHeader editorial mode (PageHeader two-zone layout)
    headerStripPadY: 14,   // breadcrumb + actions strip vertical padding
    headerHeroPadY:  26,   // hero body (tags + title + meta) vertical padding
  },
} as const;

// Color helper — positive = revenue (green), negative = cost (red).
export const amountColor = (v: number | null | undefined): string =>
  (v ?? 0) >= 0 ? COLORS.revenue : COLORS.cost;

// ─── Editorial palette (Phase B/C) ────────────────────────────────────────
// Source of truth for /__ds and the new editorial primitives
// (StatusDot, Eyebrow, Hairline, AmountHero, etc.).
//
// Distinct from the AntD-semantic ramp in COLORS.* — those continue to drive
// AntD ConfigProvider and existing pages. EDITORIAL.* uses Tailwind-derived
// hue names (green/red/blue/amber/teal/purple) and solid-hex text tiers,
// matching the editorial visual language.
//
// Two parallel palettes coexist deliberately during the design rollout:
//   - COLORS.success / .error / .warning / .info  → AntD-themed widgets
//   - EDITORIAL.green / .red / .amber / .blue     → Phase 2+ primitives
// Phase 5 (per-page rollout) migrates domain code from one to the other.

export const EDITORIAL = {
  // Surface
  bg:           "#f5f6f7",   // page background — slightly cooler than COLORS.bgPage
  surface:      "#ffffff",
  surfaceSoft:  "#fafbfc",
  surfaceHover: "#eef0f3",   // neutral hover (vs COLORS.bgHover which is blue-tinted)
  border:       "#e8eaed",
  borderSoft:   "#eef0f2",
  greySoft:     "#f3f4f6",   // grey-100 — default chip / unpaid pill fill

  // Text — solid hex (vs AntD's alpha-on-white in COLORS.text*)
  text:         "#1f2937",
  textSec:      "#4b5563",
  textMuted:    "#6b7280",
  textFaint:    "#9ca3af",

  // Editorial accent — warm rust (used sparingly: hero highlight, marquee accents)
  accentRust:   "#9A4824",

  // ── Green — revenue, paid, positive
  green:        "#16a34a",
  greenText:    "#166534",
  greenSoft:    "#dcfce7",
  greenSoftBg:  "#f0fdf4",
  greenBorder:  "#86efac",

  // ── Red — cost, error, overdue
  red:          "#dc2626",
  redText:      "#991b1b",
  redSoft:      "#fee2e2",
  redSoftBg:    "#fef2f2",
  redBorder:    "#fca5a5",
  danger:       "#ef4444",

  // ── Blue — links, active, primary CTA
  blue:         "#2563eb",
  blueText:     "#1e40af",
  blueSoft:     "#dbeafe",
  blueSoftBg:   "#eff6ff",
  blueBorder:   "#93c5fd",

  // ── Amber — warning, on_hold, pending
  amber:        "#d97706",
  amberText:    "#92400e",
  amberSoft:    "#fef3c7",
  amberSoftBg:  "#fffbeb",
  amberBorder:  "#fcd34d",

  // ── Teal — special status
  teal:         "#0d9488",
  tealText:     "#0f766e",
  tealSoft:     "#ccfbf1",
  tealSoftBg:   "#f0fdfa",
  tealBorder:   "#5eead4",

  // ── Purple — highlight, partial (Tailwind violet — distinct from COLORS.purple #722ed1)
  purple:       "#7c3aed",
  purpleText:   "#4c1d95",
  purpleSoft:   "#ede9fe",
  purpleSoftBg: "#f5f3ff",
  purpleBorder: "#c4b5fd",

  // ── Grey neutral — unpaid, disabled, secondary
  greyText:     "#374151",
  greySoftBg:   "#f9fafb",
  greyBorder:   "#d1d5db",

  // Money-sign colors — by reference to COLORS so the two palettes can't drift.
  positive:     COLORS.revenue,
  negative:     COLORS.cost,

  // Sidebar (dark navy chrome — Phase B BlueprintShell)
  sidebar:        "#161c2c",
  sidebarSurface: "#1d2438",
  sidebarText:    "#cbd5e1",
  sidebarTextDim: "#7c8696",
  sidebarGroup:   "#5b6478",
  sidebarHover:   "#26304a",
  sidebarActive:  "#2c3650",
} as const;

// ─── Legacy aliases (Phase A1 of token migration) ─────────────────────────
// LAYOUT_TOKENS and SEMANTIC_COLORS were the original token surface in
// src/types/constants.ts. They're re-exported here so consumers can simply
// switch their import path. Phase A2 (per-token renames to COLORS/SPACE/etc.)
// happens gradually.
//
// New code SHOULD use COLORS / SPACE / RADIUS / FONT_SIZE / LAYOUT directly.
// New code SHOULD NOT use LAYOUT_TOKENS / SEMANTIC_COLORS.

export const LAYOUT_TOKENS = {
  // Surface / Background
  surfaceSubtle:    COLORS.bgSubtle,
  surfaceHover:     COLORS.bgActive,
  surfacePanel:     COLORS.bgPage,
  surfaceCard:      COLORS.bgCard,
  txColumnLoadTint:   COLORS.txColumnLoadTint,
  txColumnUnloadTint: COLORS.txColumnUnloadTint,
  transportTint:    COLORS.transportTint,
  warningBg:        COLORS.warningBg,
  warningBgStrong:  COLORS.warningBgStrong,
  successBg:        COLORS.successBg,
  pinnedBg:         COLORS.pinnedBg,
  errorRowBg:       COLORS.errorBg,

  // Side direction
  sideLoadBg:        COLORS.sideLoadBg,
  sideLoadBorder:    COLORS.sideLoadBorder,
  sideLoadGradient:  COLORS.sideLoadGradient,
  sideUnloadBg:      COLORS.sideUnloadBg,
  sideUnloadBorder:  COLORS.sideUnloadBorder,
  sideUnloadGradient:COLORS.sideUnloadGradient,

  // Borders / Dividers
  borderDefault:    COLORS.borderDefault,
  divider:          COLORS.divider,
  borderWarning:    COLORS.borderWarning,
  borderSuccess:    COLORS.borderSuccess,
  borderPinned:     COLORS.borderPinned,
  borderConflict:   COLORS.borderConflict,
  expiredRowBg:     COLORS.expiredRowBg,

  // Drag highlight
  draggableHoverBg: COLORS.draggableHoverBg,

  // Icons
  iconNeutral:      COLORS.iconNeutral,
  iconMuted:        COLORS.iconMuted,
  iconDisabled:     COLORS.iconDisabled,

  // Input border
  inputBorder:      COLORS.borderStrong,

  // Radius
  radiusLg:         RADIUS.lg,
  radiusSm:         RADIUS.sm,
  radiusXs:         RADIUS.xs,

  // Spacing
  paddingHeader:    LAYOUT.paddingHeader,
  paddingSection:   LAYOUT.paddingSection,
  paddingCompact:   LAYOUT.paddingCompact,
  paddingHeaderXY:  LAYOUT.paddingHeaderXY,
  paddingSectionXY: LAYOUT.paddingSectionXY,
  paddingCompactXY: LAYOUT.paddingCompactXY,
  gapDefault:       SPACE.lg,
  gapTight:         SPACE.sm,
  gapCompact:       SPACE.xs,
  marginSection:    SPACE.lg,
  marginFormItem:   LAYOUT.marginFormItem,
  marginHeaderBottom: LAYOUT.marginHeaderBottom,
  space: SPACE,
  spacingXs:      SPACE.xs,
  spacingSmall:   SPACE.sm,
  spacingMedium:  SPACE.md,
  spacingDefault: SPACE.lg,
  spacingLarge:   SPACE.xl,

  // Peek panel
  peekDefaultWidth: LAYOUT.peekDefaultWidth,
  peekMinWidth:     LAYOUT.peekMinWidth,
  peekMaxWidth:     LAYOUT.peekMaxWidth,

  // Toolbar
  toolbarSearchWidth:  LAYOUT.toolbarSearchWidth,
  toolbarInputFillBg:  COLORS.toolbarInputFillBg,

  // Typography
  fontSizeMicro:    FONT_SIZE.micro,
  fontSizeLabel:    FONT_SIZE.label,
  fontSizeSmall:    FONT_SIZE.small,
  fontSizeBody:     FONT_SIZE.body,
  fontSizeNormal:   FONT_SIZE.normal,
  fontSizeSubheading: FONT_SIZE.subheading,
  fontSizeLarge:    FONT_SIZE.large,
} as const;

export const SEMANTIC_COLORS = {
  cost:       COLORS.cost,
  revenue:    COLORS.revenue,
  correction: COLORS.correction,
  neutral:    COLORS.neutral,
  warning:    COLORS.warningStrong,   // legacy "warning" was orange (#fa8c16)
  valid:      COLORS.success,
  danger:     COLORS.error,
  info:       COLORS.info,
  amber:      COLORS.warning,         // legacy "amber" was warning yellow (#faad14)
  primary:    COLORS.primary,
  cyan:       COLORS.cyan,
  purple:     COLORS.purple,
  textPrimary:   COLORS.text,
  textSecondary: COLORS.textSecondary,
  textTertiary:  COLORS.textTertiary,
} as const;
